import React, { useRef, useEffect } from "react";

// Lightweight, dependency-free 3D particle-depth field rendered on canvas.
// Particles drift in a 3D space (x, y, z), are projected with perspective,
// connected by lines when near, and react subtly to mouse movement (parallax).
const HeroCanvas = () => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const COUNT = window.innerWidth < 768 ? 45 : 90;
    const FOV = 320;
    const particles = [];

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };

    const rnd = (a, b) => a + Math.random() * (b - a);
    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: rnd(-260, 260),
        y: rnd(-180, 180),
        z: rnd(-260, 260),
        vx: rnd(-0.18, 0.18),
        vy: rnd(-0.18, 0.18),
        vz: rnd(-0.18, 0.18),
        big: Math.random() > 0.82,
      });
    }

    const project = (p, cx, cy, ox, oy) => {
      const scale = FOV / (FOV + p.z + 280);
      return {
        sx: cx + (p.x + ox) * scale,
        sy: cy + (p.y + oy) * scale,
        scale,
      };
    };

    const draw = () => {
      resizeIfNeeded();
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const ox = mouse.current.x * 36;
      const oy = mouse.current.y * 26;

      const pts = particles.map((p) => {
        p.x += p.vx; p.y += p.vy; p.z += p.vz;
        if (p.x > 260 || p.x < -260) p.vx *= -1;
        if (p.y > 180 || p.y < -180) p.vy *= -1;
        if (p.z > 260 || p.z < -260) p.vz *= -1;
        return { ...project(p, cx, cy, ox, oy), big: p.big };
      });

      // connections
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].sx - pts[j].sx;
          const dy = pts[i].sy - pts[j].sy;
          const dist = Math.hypot(dx, dy);
          if (dist < 120) {
            const a = (1 - dist / 120) * 0.4 * Math.min(pts[i].scale, pts[j].scale);
            ctx.strokeStyle = `rgba(238,90,36,${a})`;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(pts[i].sx, pts[i].sy);
            ctx.lineTo(pts[j].sx, pts[j].sy);
            ctx.stroke();
          }
        }
      }
      // nodes
      pts.forEach((p) => {
        const r = (p.big ? 3.2 : 1.6) * p.scale;
        const alpha = Math.max(0.15, Math.min(1, p.scale));
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
        ctx.fillStyle = p.big
          ? `rgba(244,123,79,${alpha})`
          : `rgba(255,255,255,${alpha * 0.7})`;
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    };

    let lastW = 0, lastH = 0;
    const resizeIfNeeded = () => {
      if (canvas.clientWidth !== lastW || canvas.clientHeight !== lastH) {
        lastW = canvas.clientWidth; lastH = canvas.clientHeight; resize();
      }
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      mouse.current.y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };

    resize();
    draw();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
};

export default HeroCanvas;
